"use client";

import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ChevronDown } from "lucide-react";
import TextGenerateEffect from "@/components/TextGenerateEffect";

gsap.registerPlugin(ScrollTrigger);

// Mobile URL-bar collapse/expand used to retrigger ScrollTrigger.refresh(),
// which caused pinned sections to visibly jump and leave a gap under the
// pin. ignoreMobileResize keeps pin ranges stable while the bar animates.
if (typeof window !== "undefined") {
  ScrollTrigger.config({ ignoreMobileResize: true });
}

const BACK_CARDS = [
  {
    id: "1",
    title: "Modern-Day Renaissance Man",
    body: "From triathlons to Catan championships, he treats every arena like a stage for elite performance.",
    image:
      "https://for-media.b-cdn.net/ozgurmd/CATAN%20Americas%202026_winner_sm.png",
  },
  {
    id: "2",
    title: "Proven Authority",
    body: "Honors, lectures, and publications stack into a track record few in medicine can match.",
    image: "https://for-media.b-cdn.net/ozgurmd/burak-hoag.jpg",
  },
  {
    id: "3",
    title: "Relentless Innovator",
    body: "Pioneering minimally invasive techniques and technologies that quietly reset the standard of spine care.",
    image: "https://for-media.b-cdn.net/ozgurmd/12980.jpg",
  },
  {
    id: "4",
    title: "World-Renowned Neurosurgeon",
    body: "Operating daily at the razor’s edge, where precision, pressure, and patient outcomes converge.",
    image: "https://for-media.b-cdn.net/ozgurmd/burak-ozgur-headshot-2.JPG",
  },
];

const CARD_FLIP_TILT_ANGLES = [-10, -20, -5, 10];
const CARD_DISMISS_TILT_ANGLES = [-50, -60, -45, 50];
const CARDS_ENTER_END = 100;
const CARD_FLIP_TRIGGER = 200;
const CARD_DISMISS_START = 300;
const CARD_DISMISS_DURATION = 100;

export default function MaximaTherapyStickyCards() {
  const sectionRef = useRef(null);
  // Tied to the GSAP flip/conceal — drives TextGenerateEffect on the
  // back cards so the copy word-reveals right as the cards turn to face
  // the viewer (and re-plays if the user scrolls back up and the flip
  // reverses).
  const [backRevealed, setBackRevealed] = useState(false);
  // Drives the outro headline word-reveal once the last card has
  // dismissed. Sandwiches the card stack between the intro and outro.
  const [outroVisible, setOutroVisible] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const ctx = gsap.context(() => {
      const stickyCards = gsap.utils.toArray(".mt-sc-card");
      const frontStickyCard = section.querySelector(".mt-sc-card-front");
      const backStickyCards = gsap.utils.toArray(".mt-sc-card-back");
      const heroHeadline = section.querySelector(".mt-sc-hero-intro");
      const outroHeadline = section.querySelector(".mt-sc-hero-outro");
      const stickyCardCount = backStickyCards.length;
      if (!frontStickyCard || !heroHeadline || !stickyCardCount) return;

      // Start the outro below the center of the section so it can slide
      // up into view after the last card dismisses.
      if (outroHeadline) gsap.set(outroHeadline, { yPercent: 100 });

      const totalScrollSvh = CARD_DISMISS_START + stickyCardCount * CARD_DISMISS_DURATION;
      const svhToProgress = (svh) => svh / totalScrollSvh;
      const totalScroll = window.innerHeight * (totalScrollSvh / 100);

      const cardDismissRanges = Array.from({ length: stickyCardCount }, (_, i) => {
        const dismissOrder = stickyCardCount - 1 - i;
        return [
          svhToProgress(CARD_DISMISS_START + dismissOrder * CARD_DISMISS_DURATION),
          svhToProgress(CARD_DISMISS_START + (dismissOrder + 1) * CARD_DISMISS_DURATION),
        ];
      });

      gsap.set(frontStickyCard, { rotationY: 0 });
      gsap.set(backStickyCards, { rotationY: -180 });

      let isFlipped = false;

      const revealBackCards = () => {
        gsap.to(frontStickyCard, {
          rotationY: 180,
          duration: 1,
          ease: "elastic.out(1,0.5)",
        });
        backStickyCards.forEach((card, i) => {
          gsap.to(card, {
            rotationY: 0,
            rotationZ: CARD_FLIP_TILT_ANGLES[i],
            duration: 1,
            ease: "elastic.out(1,0.5)",
          });
        });
      };

      const concealBackCards = () => {
        gsap.to(frontStickyCard, {
          rotationY: 0,
          duration: 1,
          ease: "elastic.out(1,0.5)",
        });
        backStickyCards.forEach((card) => {
          gsap.to(card, {
            rotationY: -180,
            rotationZ: 0,
            duration: 1,
            ease: "elastic.out(1,0.5)",
          });
        });
      };

      ScrollTrigger.create({
        trigger: section,
        start: "top top",
        end: `+=${totalScroll}px`,
        pin: true,
        pinSpacing: true,
        scrub: true,
        onUpdate: ({ progress }) => {
          // Publish a dock-active flag on the section so ContextualDock
          // can dismiss the #story CTA before the pin fully releases —
          // otherwise it lingers through the trailing pin spacer.
          section.dataset.ctxActive = progress < 0.72 ? "true" : "false";
          const enterProgress = gsap.utils.clamp(
            0,
            1,
            gsap.utils.mapRange(0, svhToProgress(CARDS_ENTER_END), 0, 1, progress)
          );

          gsap.set(stickyCards, {
            y: `${gsap.utils.mapRange(0, 1, 50, -50, enterProgress)}%`,
          });
          gsap.set(heroHeadline, {
            y: `${gsap.utils.mapRange(0, 1, 0, -100, enterProgress)}%`,
          });

          if (outroHeadline) {
            // Slide the outro up from below starting as the last card
            // begins to clear. Runs over the last ~15% of the pin.
            const outroProgress = gsap.utils.clamp(
              0,
              1,
              gsap.utils.mapRange(0.85, 1, 0, 1, progress)
            );
            gsap.set(outroHeadline, {
              yPercent: gsap.utils.mapRange(0, 1, 100, 0, outroProgress),
            });
            setOutroVisible(outroProgress > 0.1);
          }

          if (progress > svhToProgress(CARD_FLIP_TRIGGER) && !isFlipped) {
            revealBackCards();
            setBackRevealed(true);
            isFlipped = true;
          } else if (progress <= svhToProgress(CARD_FLIP_TRIGGER) && isFlipped) {
            concealBackCards();
            setBackRevealed(false);
            isFlipped = false;
          }

          backStickyCards.forEach((card, i) => {
            const [dismissStart, dismissEnd] = cardDismissRanges[i];
            const dismissProgress = gsap.utils.clamp(
              0,
              1,
              gsap.utils.mapRange(dismissStart, dismissEnd, 0, 1, progress)
            );
            gsap.set(card, {
              y: `${gsap.utils.mapRange(0, 1, -50, -250, dismissProgress)}%`,
              rotation: gsap.utils.mapRange(
                0,
                1,
                CARD_FLIP_TILT_ANGLES[i],
                CARD_DISMISS_TILT_ANGLES[i],
                dismissProgress
              ),
            });
          });
        },
      });
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} id="story" className="mt-sc-hero">
      <div className="mt-sc-hero-content mt-sc-hero-intro">
        <TextGenerateEffect
          as="h1"
          words="Burak stands as a rare outlier in a world of specialists — a powerhouse dominating the most demanding disciplines known to man."
          className="mt-sc-hero-headline"
          duration={0.6}
          staggerDelay={0.04}
        />
      </div>

      <div className="mt-sc-hero-content mt-sc-hero-outro">
        <TextGenerateEffect
          as="h2"
          words="With an approachable style, he bridges the gap between medical science and everyday health, making him an ideal guest for television, podcasts, and live events."
          className="mt-sc-hero-headline"
          duration={0.7}
          staggerDelay={0.04}
          trigger={outroVisible}
        />
      </div>

      <div className="mt-sc-sticky-cards">
        <article className="mt-sc-card mt-sc-card-front">
          <TextGenerateEffect
            as="h3"
            words="Burak Ozgur, MD"
            duration={0.7}
            staggerDelay={0.07}
            filter={false}
          />
          <TextGenerateEffect
            as="p"
            words="Follow along to witness the pursuit of the impossible."
            duration={0.7}
            staggerDelay={0.04}
            filter={false}
          />
          <div className="mt-sc-icon mt-sc-front-icon">
            <ChevronDown size={24} />
          </div>
        </article>

        {BACK_CARDS.map((card) => (
          <article
            key={card.id}
            className={`mt-sc-card mt-sc-card-back mt-sc-card-${card.id} mt-sc-card-photo`}
          >
            <div className="mt-sc-card-bg" aria-hidden="true">
              <img src={card.image} alt="" />
            </div>
            <div className="mt-sc-card-copy">
              <TextGenerateEffect
                as="h3"
                words={card.title}
                duration={0.7}
                staggerDelay={0.07}
                filter={false}
                trigger={backRevealed}
              />
              <TextGenerateEffect
                as="p"
                words={card.body}
                duration={0.7}
                staggerDelay={0.03}
                filter={false}
                trigger={backRevealed}
              />
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
